import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Globe, Activity, ArrowRight, TrendingUp, ClipboardCheck, 
  LayoutDashboard, BarChart3, LogOut, Settings, User, Crown,
  ChevronRight, ChevronLeft, Users, Briefcase, Target, Menu, Lock, FileText, Layers, Sparkles, Shield, FileCheck
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL;

// Marketing Modules
const marketingModules = [
  {
    id: "master-dashboard",
    title: "Master Dashboard",
    subtitle: "TIER 0 - Data Consolidada",
    description: "Dashboard centralizado con toda la data homologada y estandarizada de la agencia.",
    icon: LayoutDashboard,
    color: "from-amber-500 to-orange-600",
    bgColor: "bg-amber-50",
    features: ["Data estandarizada", "Todos los clientes", "Power BI integrado"],
    badge: "TIER 0",
    badgeColor: "bg-amber-100 text-amber-700",
    buttonText: "Acceder",
    author: "Datahub Core Team"
  },
  {
    id: "country-sentinel",
    title: "Country Sentinel",
    subtitle: "Control de Países",
    description: "Detecta cuando tus campañas de marketing se muestran en países no autorizados.",
    icon: Globe,
    color: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-50",
    features: ["Monitoreo geográfico", "Alertas por país"],
    badge: "Activo",
    badgeColor: "bg-emerald-100 text-emerald-700",
    author: "Datahub Core Team"
  },
  {
    id: "anomaly-detection",
    title: "Detección de Anomalías",
    subtitle: "Análisis Z-Score",
    description: "Identifica desviaciones significativas en métricas clave usando análisis estadístico.",
    icon: Activity,
    color: "from-purple-500 to-pink-500",
    bgColor: "bg-purple-50",
    features: ["CTR, Conversiones, Costos", "Semáforos de severidad"],
    badge: "Activo",
    badgeColor: "bg-emerald-100 text-emerald-700",
    author: "Datahub Core Team"
  },
  {
    id: "percentile-analysis",
    title: "Análisis de Percentiles",
    subtitle: "¿Día Bueno o Malo?",
    description: "Compara cada día contra tu histórico para saber si fue excepcional o problemático.",
    icon: BarChart3,
    color: "from-cyan-500 to-blue-500",
    bgColor: "bg-cyan-50",
    features: ["Comparación histórica", "Percentiles por métrica"],
    badge: "Nuevo",
    badgeColor: "bg-cyan-100 text-cyan-700",
    author: "Datahub Core Team"
  },
  {
    id: "qa-validation",
    title: "QA de Anuncios",
    subtitle: "Validación y Control",
    description: "Revisa y valida anuncios recién creados o modificados.",
    icon: ClipboardCheck,
    color: "from-teal-500 to-emerald-500",
    bgColor: "bg-teal-50",
    features: ["Estado de aprobación", "Ad Strength"],
    badge: "Activo",
    badgeColor: "bg-emerald-100 text-emerald-700",
    author: "Datahub Core Team"
  },
  {
    id: "budget-monitor",
    title: "Monitor de Presupuesto",
    subtitle: "Próximamente",
    description: "Controla el gasto de tus campañas y recibe alertas proactivas.",
    icon: TrendingUp,
    color: "from-slate-400 to-slate-500",
    bgColor: "bg-slate-50",
    features: ["Límites personalizados", "Proyecciones"],
    badge: "Próximamente",
    badgeColor: "bg-slate-100 text-slate-500",
    disabled: true,
    author: "Datahub Core Team"
  },
  {
    id: "anomaly-detection-v2",
    title: "Anomalías v2",
    subtitle: "Z-Score Adaptativo",
    description: "Detección avanzada de anomalías con umbrales adaptativos por día de la semana y segmentación por variabilidad.",
    icon: Activity,
    color: "from-orange-500 to-red-500",
    bgColor: "bg-orange-50",
    features: ["8 métricas", "Umbrales adaptativos", "Multi-plataforma"],
    badge: "Activo",
    badgeColor: "bg-emerald-100 text-emerald-700",
    author: "Datahub Core Team"
  },
  {
    id: "percentile-analysis-v2",
    title: "Percentiles v2",
    subtitle: "¿Día Bueno o Malo?",
    description: "Compara el rendimiento de cada día contra los percentiles históricos por día de la semana.",
    icon: BarChart3,
    color: "from-cyan-500 to-blue-500",
    bgColor: "bg-cyan-50",
    features: ["8 métricas", "Percentiles P10-P90", "Multi-plataforma"],
    badge: "Activo",
    badgeColor: "bg-emerald-100 text-emerald-700",
    author: "Datahub Core Team"
  },
  {
    id: "media-planner",
    title: "Media Planner",
    subtitle: "Planificación Digital",
    description: "Analiza briefs con IA y genera planes de medios digitales con estimaciones de rendimiento.",
    icon: FileText,
    color: "from-violet-500 to-purple-600",
    bgColor: "bg-violet-50",
    features: ["Análisis de briefs con IA", "Mix de medios", "Estimaciones"],
    badge: "Beta",
    badgeColor: "bg-violet-100 text-violet-700",
    author: "Irene Espinoza"
  },
  {
    id: "slide-validator",
    title: "Slide Validator",
    subtitle: "Validación Ejecutiva de Slides",
    description: "Analiza slides de reporte con 3 expertos IA: CMO Financiero, Abogado del Diablo y Editor McKinsey. Soporta texto y PPTX.",
    icon: FileCheck,
    color: "from-rose-500 to-pink-600",
    bgColor: "bg-rose-50",
    features: ["3 paneles de análisis", "Streaming en tiempo real", "Subida de PPTX"],
    badge: "Beta",
    badgeColor: "bg-rose-100 text-rose-700",
    author: "George"
  },
  {
    id: "qa-ad-scorer",
    title: "QA Ad Scorer B2B",
    subtitle: "Evaluador de Anuncios con IA",
    description: "Evalúa anuncios B2B con IA: score 0-100, veredicto APROBADO/CONDICIONAL/RECHAZADO y breakdown de 6 criterios de conversión.",
    icon: FileCheck,
    color: "from-orange-500 to-amber-600",
    bgColor: "bg-orange-50",
    features: ["Score 0-100", "Análisis visual + copy", "6 criterios B2B"],
    badge: "Nuevo",
    badgeColor: "bg-orange-100 text-orange-700",
    author: "Datahub"
  },
  {
    id: "qa-full-funnel",
    title: "QA Ad Evaluador Full Funnel",
    subtitle: "B2C Emprendedor & B2B PyME",
    description: "Evalúa anuncios por etapa de funnel (Awareness/Consideration/Conversión) y audiencia B2C/B2B con contexto de campaña exportación LATAM.",
    icon: FileCheck,
    color: "from-teal-500 to-cyan-600",
    bgColor: "bg-teal-50",
    features: ["Funnel Awareness → Conversión", "B2C Emprendedor / B2B PyME", "Historial de evaluaciones"],
    badge: "Nuevo",
    badgeColor: "bg-teal-100 text-teal-700",
    author: "Datahub"
  },
  {
    id: "campaign-structure",
    title: "Campaign Structure",
    subtitle: "Jerarquia y Ad Groups",
    description: "Mapa jerarquico de campanas y ad groups. Identifica estructuras suboptimas y distribucion de gasto.",
    icon: Layers,
    color: "from-indigo-500 to-purple-600",
    bgColor: "bg-indigo-50",
    features: ["Jerarquia", "Ad Groups", "Drill-down"],
    badge: "Nuevo",
    badgeColor: "bg-indigo-100 text-indigo-700",
    author: "Datahub Core Team ft. Patricia Mora"
  },
  {
    id: "campaign-performance",
    title: "Campaign Performance",
    subtitle: "Rendimiento en Tiempo Real",
    description: "Analisis de rendimiento por campana con ROAS, CPA, CTR y mas. Multi-plataforma, sin precalculos.",
    icon: BarChart3,
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-50",
    features: ["On-the-fly", "ROAS / CPA / CTR", "Multi-plataforma"],
    badge: "Nuevo",
    badgeColor: "bg-blue-100 text-blue-700",
    author: "Datahub Core Team ft. Patricia Mora"
  },
  {
    id: "budget-utilization",
    title: "Budget Utilization",
    subtitle: "Eficiencia del Gasto",
    description: "Concentracion de presupuesto, campanas sobre/sub-utilizadas, gasto ineficiente y oportunidades de optimizacion.",
    icon: TrendingUp,
    color: "from-emerald-500 to-teal-600",
    bgColor: "bg-emerald-50",
    features: ["Concentracion", "Eficiencia ROAS", "Multi-plataforma"],
    badge: "Nuevo",
    badgeColor: "bg-emerald-100 text-emerald-700",
    author: "Datahub Core Team ft. Patricia Mora"
  },
  {
    id: "performance-alerts",
    title: "Performance Alerts",
    subtitle: "Monitoreo Automatico",
    description: "Crea alertas sobre metricas de performance. Recibe notificaciones cuando se cumplen las condiciones.",
    icon: Activity,
    color: "from-amber-500 to-orange-500",
    bgColor: "bg-amber-50",
    features: ["Reglas personalizadas", "Email + In-app", "Evaluacion diaria"],
    badge: "Nuevo",
    badgeColor: "bg-amber-100 text-amber-700",
    author: "Datahub Core Team"
  },
  {
    id: "brand-render",
    title: "BrandRender",
    subtitle: "AI Visual Branding Studio",
    description: "Genera imagenes de producto con IA: bolsas, cajas y latas brandeadas. Editor y combinador visual integrados.",
    icon: Sparkles,
    color: "from-blue-600 to-indigo-700",
    bgColor: "bg-blue-50",
    features: ["Generacion IA", "Editing", "Combination"],
    badge: "Nuevo",
    badgeColor: "bg-blue-100 text-blue-700",
    author: "LDM",
    externalUrl: "https://render.ldm.gg/",
    buttonText: "Abrir BrandRender"
  },
  {
    id: "safe-zones",
    title: "Safe Zones",
    subtitle: "AdOps Safe Zone Tool",
    description: "Valida zonas seguras en creatividades publicitarias para evitar que elementos clave queden recortados en los formatos de cada plataforma.",
    icon: Shield,
    color: "from-slate-700 to-slate-900",
    bgColor: "bg-slate-100",
    features: ["Multi-formato", "Preview visual", "AdOps"],
    badge: "Nuevo",
    badgeColor: "bg-slate-200 text-slate-700",
    author: "Ernesto Corona"
  }
];

// Operations Modules
const operationsModules = [
  {
    id: "workload-analysis",
    title: "Carga de Trabajo",
    subtitle: "Análisis de ClickUp",
    description: "Visualiza y analiza la carga de trabajo de tu equipo, identifica cuellos de botella y tareas vencidas.",
    icon: Users,
    color: "from-orange-500 to-red-500",
    bgColor: "bg-orange-50",
    features: ["Balance de carga", "Tickets vencidos", "Aging analysis"],
    badge: "Nuevo",
    badgeColor: "bg-orange-100 text-orange-700",
    author: "Datahub Core Team"
  }
];

// Sidebar categories
const categories = [
  {
    id: "marketing",
    title: "Marketing",
    icon: Target,
    color: "text-blue-500",
    bgColor: "bg-blue-50",
    modules: marketingModules
  },
  {
    id: "operations",
    title: "Operativos",
    icon: Briefcase,
    color: "text-orange-500",
    bgColor: "bg-orange-50",
    modules: operationsModules
  }
];

export const AlertTypeSelector = ({ onSelectType, currentUser, onLogout, sidebarCollapsed: externalSidebarCollapsed, onSidebarToggle }) => {
  const [selectedCategory, setSelectedCategory] = useState("marketing");
  // Use external state if provided, otherwise use local state
  const [localSidebarCollapsed, setLocalSidebarCollapsed] = useState(false);
  const sidebarCollapsed = externalSidebarCollapsed !== undefined ? externalSidebarCollapsed : localSidebarCollapsed;
  const setSidebarCollapsed = onSidebarToggle || setLocalSidebarCollapsed;
  
  const [systemConfig, setSystemConfig] = useState({
    notifications_enabled: true,
    marketing_enabled: true,
    operations_enabled: true,
    fivetran_enabled: true,
    module_master_dashboard: true,
    module_campaign_performance: true,
    module_country_sentinel: true,
    module_anomaly_detection: true,
    module_percentile_analysis: true,
    module_qa_validation: true,
    module_anomaly_detection_v2: true,
    module_percentile_analysis_v2: true,
    module_media_planner: true,
    module_campaign_structure: true,
    module_budget_utilization: true,
    module_performance_alerts: true,
    module_budget_monitor: false,
    module_qa_ad_scorer: true,
    module_qa_full_funnel: true,
  });

  useEffect(() => {
    // Fetch system config to know which modules are enabled
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/system-config/public`);
        setSystemConfig(response.data);
      } catch (error) {
        console.error("Error fetching system config:", error);
      }
    };
    fetchConfig();
  }, []);

  const currentCategory = categories.find(c => c.id === selectedCategory);
  
  // Hardcoded visible modules
  const VISIBLE_MODULES = new Set([
    "master-dashboard",
    "anomaly-detection-v2",
    "percentile-analysis-v2",
    "media-planner",
    "campaign-structure",
    "campaign-performance",
    "budget-utilization",
    "performance-alerts",
    "brand-render",
    "safe-zones",
    "slide-validator",
    "workload-analysis",
    "qa-ad-scorer",
    "qa-full-funnel",
  ]);
  
  const currentModules = (currentCategory?.modules || []).filter(m => VISIBLE_MODULES.has(m.id));
  
  // Categories are always enabled
  const isCategoryDisabled = () => false;

  const isModuleDisabled = () => false;

  return (
    <div className="min-h-screen bg-[#e8eef3] flex pb-14">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white/70 backdrop-blur-sm border-r border-slate-200/50 flex flex-col transition-all duration-300 sticky top-0 h-screen z-30`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full overflow-hidden shadow-sm ring-2 ring-indigo-500/20">
                  <img 
                    src="/endavant-logo.jpg" 
                    alt="Endavant" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="font-bold text-slate-800">Datahub</span>
              </div>
            )}
            {sidebarCollapsed && (
              <div className="w-8 h-8 rounded-full overflow-hidden shadow-sm ring-2 ring-indigo-500/20 mx-auto">
                <img 
                  src="/endavant-logo.jpg" 
                  alt="Endavant" 
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {!sidebarCollapsed && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            {sidebarCollapsed && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 mx-auto mt-2"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Category Navigation */}
        <nav className="flex-1 p-3 space-y-2">
          <p className={`text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 ${sidebarCollapsed ? 'text-center' : 'px-3'}`}>
            {sidebarCollapsed ? '•••' : 'Categorías'}
          </p>
          {categories.map((category) => {
            const Icon = category.icon;
            const isSelected = selectedCategory === category.id;
            const isDisabled = isCategoryDisabled(category.id);
            return (
              <button
                key={category.id}
                onClick={() => !isDisabled && setSelectedCategory(category.id)}
                disabled={isDisabled}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isDisabled
                    ? 'opacity-50 cursor-not-allowed bg-slate-100 text-slate-400'
                    : isSelected 
                      ? `${category.bgColor} ${category.color} font-medium` 
                      : 'text-slate-600 hover:bg-slate-100'
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
              >
                {isDisabled ? (
                  <Lock className="h-5 w-5 text-slate-400" />
                ) : (
                  <Icon className={`h-5 w-5 ${isSelected ? category.color : ''}`} />
                )}
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-left">{category.title}</span>
                    {isDisabled ? (
                      <Badge className="text-[10px] bg-slate-200 text-slate-500">
                        Desactivado
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        {category.modules.filter(m => VISIBLE_MODULES.has(m.id)).length}
                      </Badge>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Section at Bottom */}
        <div className="p-3 border-t border-slate-100">
          {sidebarCollapsed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="w-full">
                  <User className="h-5 w-5 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="right">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{currentUser?.name}</p>
                    <p className="text-xs text-slate-500">{currentUser?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {currentUser?.role === 'admin' && (
                  <DropdownMenuItem onClick={() => onSelectType('admin')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Administración
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={onLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm">
                  {currentUser?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{currentUser?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{currentUser?.email}</p>
                </div>
              </div>
              <div className="flex gap-2">
                {currentUser?.role === 'admin' && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 text-xs"
                    onClick={() => onSelectType('admin')}
                  >
                    <Settings className="h-3 w-3 mr-1" />
                    Admin
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={onLogout}
                >
                  <LogOut className="h-3 w-3 mr-1" />
                  Salir
                </Button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <div className="border-b border-slate-200/50 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  {currentCategory && (
                    <div className={`p-2 rounded-lg ${currentCategory.bgColor}`}>
                      <currentCategory.icon className={`h-5 w-5 ${currentCategory.color}`} />
                    </div>
                  )}
                  <div>
                    <h1 className="text-xl font-bold text-slate-900">
                      Módulos de {currentCategory?.title}
                    </h1>
                    <p className="text-sm text-slate-500">
                      {currentModules.length} herramientas disponibles
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {currentUser?.role === 'admin' && (
                  <Badge className="bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border-amber-300">
                    <Crown className="h-3 w-3 mr-1" />
                    Admin
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Module Cards */}
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentModules.map((module) => {
              const IconComponent = module.icon;
              const moduleDisabled = isModuleDisabled(module.id) || isCategoryDisabled(selectedCategory);
              return (
                <Card
                  key={module.id}
                  className={`group relative overflow-hidden transition-all duration-300 ${
                    moduleDisabled 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-xl hover:-translate-y-1'
                  }`}
                >
                  {/* Disabled overlay */}
                  {moduleDisabled && (
                    <div className="absolute inset-0 bg-slate-200/50 z-10 flex items-center justify-center">
                      <div className="bg-white rounded-lg px-3 py-2 shadow-sm flex items-center gap-2">
                        <Lock className="h-4 w-4 text-slate-500" />
                        <span className="text-sm text-slate-600 font-medium">Desactivado</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Gradient overlay - pointer-events-none to not block clicks */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${module.color} opacity-0 group-hover:opacity-5 transition-opacity pointer-events-none`} />
                  
                  <CardHeader className="pb-2 pt-4 px-4">
                    <div className="flex items-start justify-between">
                      <div className={`p-2 rounded-lg ${module.bgColor}`}>
                        <IconComponent className="h-5 w-5 text-slate-700" />
                      </div>
                      <Badge className={moduleDisabled ? "bg-slate-200 text-slate-500" : module.badgeColor}>
                        {moduleDisabled ? "Desactivado" : module.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-base mt-2">{module.title}</CardTitle>
                    <CardDescription className="text-xs text-slate-400">
                      {module.subtitle}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-3 px-4 pb-4 pt-0">
                    <p className="text-xs text-slate-500 line-clamp-2">
                      {module.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-1.5">
                      {module.features.map((feature, idx) => (
                        <span 
                          key={idx}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    {module.author && (
                      <div className="flex items-center gap-1.5 border-t border-slate-100 pt-2">
                        <div className="w-4 h-4 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                          <span className="text-[8px] text-white font-bold">{module.author.charAt(0)}</span>
                        </div>
                        <span className="text-[11px] text-slate-500">{module.author}</span>
                      </div>
                    )}

                    <Button 
                      size="sm"
                      className={`w-full ${moduleDisabled ? 'bg-slate-300 cursor-not-allowed' : `bg-gradient-to-r ${module.color}`} text-white transition-shadow`}
                      onClick={() => {
                        if (moduleDisabled) return;
                        if (module.externalUrl) {
                          window.open(module.externalUrl, "_blank", "noopener,noreferrer");
                        } else {
                          onSelectType(module.id);
                        }
                      }}
                      disabled={moduleDisabled}
                    >
                      {moduleDisabled ? (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          No disponible
                        </>
                      ) : (
                        <>
                          {module.buttonText || 'Acceder'}
                          <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Footer info */}
          <div className="mt-12 mb-16 text-center">
            <p className="text-sm text-slate-500">
              {selectedCategory === 'marketing' 
                ? 'Todos los módulos se integran directamente con tu workspace de Databricks'
                : 'Datos sincronizados en tiempo real desde ClickUp'
              }
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};
